/**
 * Interface localisation.
 *
 * SCOPE — read this before adding a language anywhere else in the codebase.
 * This layer translates the APPLICATION INTERFACE only. It has no bearing on the documents the
 * system issues: devis, factures, avoirs, bons de livraison, factures récapitulatives and bons
 * de retrait are French fiscal artefacts and are generated in French whatever the signed-in user
 * has chosen here. Those live in the backend (services/pdf/*, DocumentEmailService) and are
 * deliberately unreachable from this module — see backend migration 091.
 *
 * DEFAULT — French. English is the opt-out, chosen per account on /profile and stored on the
 * user row (users.locale), so it travels with the person rather than with the browser.
 *
 * RESOURCE LAYOUT — one small JSON file per functional area, per language:
 *
 *     locales/fr/orders.json  →  keys under  orders.*
 *     locales/en/orders.json  →  keys under  orders.*
 *
 * They are globbed eagerly and merged into a single namespace, so adding an area is a matter of
 * dropping two files in — nothing here needs editing. Components just call
 * `const { t } = useTranslation()` and `t('orders.title')`.
 *
 * The English catalogue is the ORIGINAL copy that shipped before this layer existed, so English
 * output is unchanged by localisation and French is a pure addition.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LOCALES = ['fr', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'fr';

/** Cached client-side so the first paint after a reload is already in the right language, before
 *  /me/capabilities has answered. The user row remains the source of truth (see localeStore). */
export const LOCALE_STORAGE_KEY = 'mecadepot.locale';

export const LOCALE_LABELS: Record<Locale, string> = {
  fr: 'Français',
  en: 'English',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Area name → strings, for one language. The area becomes the key prefix. */
type Area = Record<string, unknown>;

function bundleFor(lang: Locale): Area {
  const modules = import.meta.glob<{ default: Area }>('./locales/*/*.json', { eager: true });
  const bundle: Area = {};
  for (const [path, mod] of Object.entries(modules)) {
    // './locales/fr/orders.json' → ['fr', 'orders']
    const match = /\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
    if (!match || match[1] !== lang) continue;
    bundle[match[2]] = mod.default;
  }
  return bundle;
}

function initialLocale(): Locale {
  if (typeof localStorage === 'undefined') return DEFAULT_LOCALE;
  const cached = localStorage.getItem(LOCALE_STORAGE_KEY);
  return isLocale(cached) ? cached : DEFAULT_LOCALE;
}

void i18next.use(initReactI18next).init({
  lng: initialLocale(),
  // A key missing from the French catalogue falls back to the English original rather than
  // rendering a raw dotted key on screen — an untranslated label is a blemish, a visible key is
  // a bug. The reverse holds for English so neither catalogue can produce one.
  fallbackLng: { fr: ['en'], en: ['fr'], default: [DEFAULT_LOCALE] },
  resources: { fr: { translation: bundleFor('fr') }, en: { translation: bundleFor('en') } },
  // React escapes everything it renders; letting i18next escape too would double-encode the
  // apostrophes and accented characters French copy is full of.
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** Apply a locale to i18next, the cache and the document element. */
export function applyLocale(locale: Locale): void {
  if (i18next.language !== locale) void i18next.changeLanguage(locale);
  if (typeof localStorage !== 'undefined') localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

applyLocale(initialLocale());

export default i18next;
